import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {computeIsDraftComment} from '../../../helpers/models/draftCommentHelpers';
import {isDraftMessage} from '../../../helpers/models/messageHelpers';
import {ActionBase, ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ApiDuplicatedConversationsDataGroup, DataGroupModel} from '../../../models/api/dataGroupModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {ThunkAsyncAction} from '../../appThunk';
import {getCompany} from '../company/companySelectors';
import {updateDataAction} from '../dataGroupsActions';

/*
 * Actions types.
 */

export enum ConversationsActionsEnum {
  CONVERSATION_CONTENT_UPDATED = '@@data/conversations/CONVERSATION_CONTENT_UPDATED',
  MARKED_AS_SEEN = '@@data/conversations/MARKED_AS_SEEN',
  CONVERSATION_ACCESS_DENIED = '@@data/conversations/CONVERSATION_ACCESS_DENIED',
  CONVERSATION_DELETED = '@@data/conversations/CONVERSATION_DELETED',
  CLEAR_CONVERSATION_CONTENT = '@@data/conversations/CLEAR_CONVERSATION_CONTENT',
  MAYBE_HIDE_CONVERSATION = '@@data/conversations/MAYBE_HIDE_CONVERSATION',
  CLEAR_HIDDEN_CONVERSATION_ID = '@@data/conversations/CLEAR_HIDDEN_CONVERSATION_ID',
  SAFETY_NET_OPTIMISTICALLY_SATISFIED = '@@data/conversations/SAFETY_NET_OPTIMISTICALLY_SATISFIED',
}

/*
 * Types.
 */

interface ConversationActionPayload {
  conversationId: number;
}
interface ConversationContentUpdatedPayload extends ConversationActionPayload {
  contentUpdatedAt: number;
  draftCommentUids: ReadonlyArray<string>;
  draftMessageUids: ReadonlyArray<string>;
}

export interface ConversationContentUpdatedAction
  extends ActionWithPayload<ConversationContentUpdatedPayload> {
  type: ConversationsActionsEnum.CONVERSATION_CONTENT_UPDATED;
}

interface ConversationMarkedAsSeenActionPayload extends ConversationActionPayload {
  seenAt: number;
}
export interface ConversationMarkedAsSeenAction
  extends ActionWithPayload<ConversationMarkedAsSeenActionPayload> {
  type: ConversationsActionsEnum.MARKED_AS_SEEN;
}

export interface ConversationAccessDeniedAction extends ActionWithPayload<ConversationActionPayload> {
  type: ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED;
}

export interface ConversationDeletedAction extends ActionWithPayload<ConversationActionPayload> {
  type: ConversationsActionsEnum.CONVERSATION_DELETED;
}

export interface ClearConversationContentAction extends ActionWithPayload<ConversationActionPayload> {
  type: ConversationsActionsEnum.CLEAR_CONVERSATION_CONTENT;
}

interface ClearHiddenConversationIdAction extends ActionBase {
  type: ConversationsActionsEnum.CLEAR_HIDDEN_CONVERSATION_ID;
}

interface MaybeHideConversationActionPayload {
  prevConversation: ConversationBaseModel;
  nextConversation: ConversationBaseModel;
}

interface MaybeSetHiddenConversationIdAction extends ActionWithPayload<MaybeHideConversationActionPayload> {
  type: ConversationsActionsEnum.MAYBE_HIDE_CONVERSATION;
}

interface SafetyNetOptimisticallySatisfiedPayload extends ConversationActionPayload {
  safetyNetId: number;
  isOptimisticallySatisfied: boolean;
}

export interface SafetyNetOptimisticallySatisfiedAction
  extends ActionWithPayload<SafetyNetOptimisticallySatisfiedPayload> {
  type: ConversationsActionsEnum.SAFETY_NET_OPTIMISTICALLY_SATISFIED;
}

/*
 * Action creators.
 */

export function conversationContentUpdatedAction(
  conversationId: number,
  contentDataGroup: DataGroupModel,
): ConversationContentUpdatedAction {
  return {
    type: ConversationsActionsEnum.CONVERSATION_CONTENT_UPDATED,
    payload: {
      conversationId,
      contentUpdatedAt: contentDataGroup.updatedAt || 0,
      draftCommentUids: (contentDataGroup.comments || []).filter(computeIsDraftComment).map((c) => c.uid),
      draftMessageUids: (contentDataGroup.messages || []).filter(isDraftMessage).map((m) => m.uid),
    },
  };
}

export function conversationMarkedAsSeenAction(
  conversationId: number,
  seenAt: number,
): ConversationMarkedAsSeenAction {
  return {
    type: ConversationsActionsEnum.MARKED_AS_SEEN,
    payload: {
      conversationId,
      seenAt,
    },
  };
}

export function conversationAccessDeniedAction(conversationId: number): ConversationAccessDeniedAction {
  return {
    type: ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED,
    payload: {conversationId},
  };
}

export function conversationDeletedAction(conversationId: number): ConversationDeletedAction {
  return {
    type: ConversationsActionsEnum.CONVERSATION_DELETED,
    payload: {conversationId},
  };
}

export function clearConversationContentAction(conversationId: number): ClearConversationContentAction {
  return {
    type: ConversationsActionsEnum.CLEAR_CONVERSATION_CONTENT,
    payload: {conversationId},
  };
}

export function clearHiddenConversationIdAction(): ClearHiddenConversationIdAction {
  return {
    type: ConversationsActionsEnum.CLEAR_HIDDEN_CONVERSATION_ID,
  };
}

export function maybeHideConversationAction(
  prevConversation: ConversationBaseModel,
  nextConversation: ConversationBaseModel,
): MaybeSetHiddenConversationIdAction {
  return {
    type: ConversationsActionsEnum.MAYBE_HIDE_CONVERSATION,
    payload: {prevConversation, nextConversation},
  };
}

/* Retrieve duplicate conversations from the given conversation id, and make them available in the store. */
export function fetchDuplicateConversationsAsyncAction(conversationId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const company = getCompany(state);

    const pathAndQuery = `${company.url}/conversations/${conversationId}/duplicates`;

    try {
      const fetchDataGroup = await fetchApiAsync(ApiDuplicatedConversationsDataGroup, pathAndQuery);

      dispatch(updateDataAction(fetchDataGroup));
    } catch (error) {
      // We don't expect errors other than HttpErrors.
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
    }
  };
}

/*
 * Union type.
 */

export type ConversationsAction =
  | ConversationMarkedAsSeenAction
  | ConversationAccessDeniedAction
  | ConversationDeletedAction
  | ClearConversationContentAction
  | ClearHiddenConversationIdAction
  | MaybeSetHiddenConversationIdAction
  | ConversationContentUpdatedAction
  | SafetyNetOptimisticallySatisfiedAction;
